<template>
  <button
    type="button"
    class="assignments-counter"
    :class="{
      'assignments-counter--red': isRed,
      'assignments-counter--busy': inProgress,
    }"
    :aria-label="ariaLabel"
    :aria-busy="inProgress ? 'true' : 'false'"
    @click="onClick"
  >
    <span v-if="hasDirect" class="assignments-counter__segment" data-name="direct-segment">
      <span
        class="assignments-counter__icon"
        :class="{ 'assignments-counter__icon--red': directIsNotActive }"
        aria-hidden="true"
      >
        <Icon name="org_icon_responsibility" size="small" />
      </span>
      <v-progress-circular
        v-if="inProgress"
        indeterminate
        :size="16"
        :width="2"
        color="#344054"
        data-name="spinner"
      />
      <span
        v-else
        class="assignments-counter__count"
        :class="{ 'assignments-counter__count--red': directIsNotActive }"
        data-name="direct-count"
      >
        {{ directCount }}
      </span>
    </span>

    <span
      v-if="showDivider"
      class="assignments-counter__divider"
      aria-hidden="true"
      data-name="divider"
    >
      /
    </span>

    <template v-if="showRoles">
      <span class="assignments-counter__segment" data-name="roles-segment">
        <span class="assignments-counter__icon" aria-hidden="true">
          <Icon name="org_icon_role" size="small" />
        </span>
        <v-progress-circular
          v-if="inProgress"
          indeterminate
          :size="16"
          :width="2"
          color="#344054"
          data-name="spinner"
        />
        <span
          v-else
          class="assignments-counter__count"
          :class="{ 'assignments-counter__count--red': rolesCount === 0 }"
          data-name="roles-count"
        >
          {{ rolesCount }}
        </span>
      </span>

      <span class="assignments-counter__arrow" aria-hidden="true" data-name="arrow">
        <Icon name="arrow_narrow_left_icon" />
      </span>

      <span class="assignments-counter__segment" data-name="responsibilities-segment">
        <span class="assignments-counter__icon" aria-hidden="true">
          <Icon name="org_icon_responsibility" size="small" />
        </span>
        <v-progress-circular
          v-if="inProgress"
          indeterminate
          :size="16"
          :width="2"
          color="#344054"
          data-name="spinner"
        />
        <span
          v-else
          class="assignments-counter__count"
          :class="{ 'assignments-counter__count--red': responsibilitiesCount === 0 }"
          data-name="responsibilities-count"
        >
          {{ responsibilitiesCount }}
        </span>
      </span>
    </template>
  </button>
</template>

<script setup>
import { computed } from "vue";
import Icon from "@components/icons/Icon.vue";

const props = defineProps({
  /** Roles attached to the position. */
  rolesCount: {
    type: Number,
    default: 0,
  },
  /** Responsibilities that come through the attached roles. */
  responsibilitiesCount: {
    type: Number,
    default: 0,
  },
  /** Responsibilities attached directly to the position. */
  directResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  /** Direct responsibilities that are not active; when present they replace the direct count and show red. */
  notActiveDirectResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  /** Recalculation running: a spinner replaces every count. */
  inProgress: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["click"]);

const directIsNotActive = computed(() => props.notActiveDirectResponsibilitiesCount > 0);

const directCount = computed(() =>
  directIsNotActive.value
    ? props.notActiveDirectResponsibilitiesCount
    : props.directResponsibilitiesCount
);

const hasDirect = computed(
  () => props.directResponsibilitiesCount > 0 || props.notActiveDirectResponsibilitiesCount > 0
);

const showRoles = computed(
  () => !hasDirect.value || props.rolesCount > 0 || props.responsibilitiesCount > 0
);

const showDivider = computed(() => hasDirect.value && showRoles.value);

const isRed = computed(
  () => !hasDirect.value && (props.rolesCount === 0 || props.responsibilitiesCount === 0)
);

const ariaLabel = computed(() => {
  if (props.inProgress) return "Recalculating roles and responsibilities";
  const parts = [];
  if (hasDirect.value) {
    parts.push(
      `${directCount.value} ${directIsNotActive.value ? "not active " : ""}direct responsibilities`
    );
  }
  if (showRoles.value) {
    parts.push(
      `${props.rolesCount} roles with ${props.responsibilitiesCount} responsibilities via roles`
    );
  }
  return parts.join(", ");
});

function onClick(event) {
  // Forward the DOM event so parent listeners can use modifiers (e.g. @click.stop).
  emit("click", event);
}
</script>

<style scoped>
.assignments-counter {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
  height: 24px;
  padding: 0 6px;
  border: 2px solid #666666;
  border-radius: 5px;
  background-color: #ffffff;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
}
.assignments-counter--red {
  border-color: #d92d20;
  background-color: #ffe0e0;
}
.assignments-counter:not(.assignments-counter--busy):hover,
.assignments-counter:not(.assignments-counter--busy):focus-visible {
  border-color: #7f56d9;
  background-color: #f2edff;
}
.assignments-counter:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
.assignments-counter__segment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.assignments-counter__count {
  color: #344054;
}
.assignments-counter__count--red {
  color: #b42318;
}
.assignments-counter__divider {
  width: 24px;
  color: #000000;
  text-align: center;
}
.assignments-counter__icon,
.assignments-counter__arrow {
  display: inline-flex;
}
.assignments-counter__arrow {
  width: 24px;
  height: 24px;
}
.assignments-counter__icon--red ::v-deep svg path {
  fill: #b42318;
  stroke: #b42318;
}
</style>
